'use client';

/**
 * Nuclear "reset this device" helper. Wipes every piece of app state so
 * the user can start from a truly blank slate without having to dig into
 * iOS/Android browser settings to clear site data.
 *
 * Wiped:
 *   - Dexie tables (projects, yarns, inventory, techniques, inspirations,
 *     counterSnapshots, charts, timeSessions)
 *   - Automerge doc shadow store (`knitlib-docs`)
 *   - Content-addressed blob store (`knitlib-blobs`)
 *   - Debug log ring buffer (`knitlib-logs`)
 *   - localStorage — account key, recovery phrase, sync room, device id,
 *     migration flags, profile, i18n lang, everything under the `knitlib-` prefix
 *   - OPFS blobs if available (quietly — best effort)
 *
 * Sync connection is closed first so no in-flight pushes race the wipe.
 */

import { db } from './db';
import { syncClient } from './syncClient';

export async function wipeAllData(): Promise<void> {
  // 1. Tear down the live sync connection and clear key material.
  try { syncClient.reset(); } catch { /* ignore */ }

  // 2. Clear Dexie tables in one transaction so rows vanish atomically.
  try {
    await db.transaction('rw', [
      db.projects,
      db.yarns,
      db.inventory,
      db.techniques,
      db.inspirations,
      db.counterSnapshots,
      db.charts,
      db.timeSessions,
    ], async () => {
      await Promise.all([
        db.projects.clear(),
        db.yarns.clear(),
        db.inventory.clear(),
        db.techniques.clear(),
        db.inspirations.clear(),
        db.counterSnapshots.clear(),
        db.charts.clear(),
        db.timeSessions.clear(),
      ]);
    });
  } catch (e) {
    console.warn('[reset] dexie clear failed', e);
  }

  // 3. Delete the auxiliary IndexedDB databases used by sync / blobs / logs / local backups.
  const auxDbs = ['knitlib-docs', 'knitlib-blobs', 'knitlib-logs', 'knitlib-local-backups'];
  for (const name of auxDbs) {
    try {
      await new Promise<void>((resolve) => {
        const req = indexedDB.deleteDatabase(name);
        req.onsuccess = () => resolve();
        req.onerror = () => resolve(); // best-effort — don't block reset on a rogue DB
        req.onblocked = () => resolve();
      });
    } catch { /* ignore */ }
  }

  // 4. Wipe every localStorage key we own. We prefix almost everything with
  //    `knitlib-`, so pattern-match and drop those, then also drop the few
  //    legacy / un-prefixed keys (language, profile cache).
  try {
    const toDelete: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith('knitlib-') || k === 'lang' || k === 'profile') {
        toDelete.push(k);
      }
    }
    for (const k of toDelete) localStorage.removeItem(k);
  } catch { /* ignore */ }

  // 5. Best-effort OPFS wipe (where supported). The blob store uses OPFS
  //    preferentially, so this cleans up any residual files.
  try {
    const root = await (navigator.storage as unknown as { getDirectory?: () => Promise<FileSystemDirectoryHandle> }).getDirectory?.();
    if (root) {
      for await (const entry of (root as unknown as AsyncIterable<FileSystemHandle>)) {
        try { await (root as unknown as { removeEntry: (n: string, o?: { recursive?: boolean }) => Promise<void> }).removeEntry(entry.name, { recursive: true }); } catch { /* ignore */ }
      }
    }
  } catch { /* ignore */ }
}
